import React, { useContext, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

// styles
import * as SC from './SalonsBookingFormStyles'
import { HomePageContainer } from '../../HomePageStyles'
import { SALONS_PAGE_MOBILE_BREAKPOINT_INT } from '../../../../styles/constants'

// hooks
import useMessages from '../../../../hooks/useMessages'
import useIsMobile from '../../../../hooks/useIsMobile'

// types
import { BookingFormType, CategoriesResponse, ConfigResponse, SalonsCategoryPageContainerQuery, SalonsListPageContainerQuery } from '../../../../types/types'
import { BaseSelectOption, SelectProps } from '../../../../atoms/Select/types'

// utils
import { DEFAULT_DATE_FORMAT, FORM, RESERVATIONS_TIME_SLOTS, SESSION_STORAGE_KEYS } from '../../../../utils/enums'
import { BOOKING_FORM_Z_INDEXES, PAGE_LINKS } from '../../../../utils/helper'
import { getBookingFormSubmitEvent, getClickToSearchBookingEvent } from '../../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../../utils/dataLayer'
import { GET_MY_LOCATION_OPTION } from '../../../../components/SalonsAndCitiesSearch/SalonsAndCitiesSearch'
import { MyLocationContext } from '../../../../utils/myLocationProvider'

// components
import HookFormField from '../../../../formFields/HookFormField'
import SalonsAndCitiesSearchFormField from '../../../../formFields/SalonsAndCitiesSearchFormField/SalonsAndCitiesSearchFormField'
import DateTimeSlotPickerBookingFormField from '../../../../formFields/DateTimeSlotPickerBookingFormField/DateTimeSlotPickerBookingFormField'
import SelectFormField from '../../../../formFields/SelectFormField/SelectFormField'

// assets
import CategoryIcon from '../../../../assets/icons/CategoryIcon'

type SalonsBookingFormProps = {
	categoriesData: CategoriesResponse
	configData: ConfigResponse
}

type IndustryValue = string
type IndustryOption = BaseSelectOption<{ image?: string }>

const IndustryOptionRender: SelectProps<IndustryValue, IndustryOption>['optionRender'] = (option) => {
	return (
		<SC.IndustryOptionWrapper>
			<SC.IndustryOptionImage $image={option.extra?.image} />
			<SC.IndustryOptionText>{option.label}</SC.IndustryOptionText>
		</SC.IndustryOptionWrapper>
	)
}

// component
const SalonsBookingForm = (props: SalonsBookingFormProps) => {
	const { categoriesData, configData } = props

	const { categories } = categoriesData
	const { maxAvResTimeSlotDateRange } = configData

	const { messages } = useMessages()
	const { locale } = useIntl()
	const isMobile = useIsMobile(SALONS_PAGE_MOBILE_BREAKPOINT_INT)

	const { myLocation } = useContext(MyLocationContext)

	const [isLoadingSearchValue, setIsLoadingSearchValue] = useState(true)

	const { control, handleSubmit, watch } = useForm<BookingFormType>({
		defaultValues: {
			city: undefined,
			availableReservations: {
				timeSlot: RESERVATIONS_TIME_SLOTS.ANY,
				dateFrom: undefined,
				dateTo: undefined
			},
			industryID: ''
		}
	})

	useEffect(() => {
		setIsLoadingSearchValue(false)
	}, [])

	const industryOptions = categories.map((industry) => {
		return {
			key: industry.id,
			value: industry.id,
			label: industry.name || industry.id,
			extra: {
				image: industry.image?.resizedImages.thumbnail
			}
		}
	})

	const getLocationParams = (city: BookingFormType['city']) => {
		const MY_LOCATION_OPTION = GET_MY_LOCATION_OPTION()
		const isUsingMyPosition = city?.value === MY_LOCATION_OPTION.value

		let lat: number | undefined
		let lon: number | undefined
		let latMy: number | undefined
		let lonMy: number | undefined
		let googlePlaceID: string | undefined

		if (isUsingMyPosition || myLocation) {
			latMy = myLocation?.latMy || city?.extra?.latitude
			lonMy = myLocation?.lonMy || city?.extra?.longitude
		}

		if (!isUsingMyPosition) {
			lat = city?.extra?.latitude
			lon = city?.extra?.longitude
			googlePlaceID = city?.value
		}

		return { lat, lon, latMy, lonMy, googlePlaceID }
	}

	const isFormStartInteractionEventSent = useRef(false)
	const formInteractionStartEventTimestamp = useRef<number | null>(null)
	const sendFormStartInteractionEvent = () => {
		// NOTE: We are attaching this event on focus on each input.
		// Alternative approach would be checking form state/form dirty fields,
		// but that would be more complicated because some form fields (city input)
		// might be re-initialized on first mount (when user has geo location enabled),
		// which causes the form to be dirty even if the user didn't interact with it.
		// Using onFocus is much safer - we are sure that the user interacted with the form.
		if (isFormStartInteractionEventSent.current) return
		pushToDataLayer(getClickToSearchBookingEvent({ isMobile }))

		isFormStartInteractionEventSent.current = true
		formInteractionStartEventTimestamp.current = Date.now()
	}

	const handleSubmitFilter = async (formValues: BookingFormType) => {
		// google analytics
		const formInteractionDuration = Date.now() - (formInteractionStartEventTimestamp.current ?? Date.now())
		const event = getBookingFormSubmitEvent({
			timing: formInteractionDuration,
			searchTerm: formValues.city?.label ?? '',
			dateFrom: formValues.availableReservations.dateFrom,
			dateTo: formValues.availableReservations.dateTo,
			timeSlot: formValues.availableReservations.timeSlot,
			category: formValues.industryID,
			subcategory: undefined
		})
		pushToDataLayer(event)

		let queryParams: Partial<SalonsCategoryPageContainerQuery | SalonsListPageContainerQuery> = { hasAvailableReservationSystem: true }

		if (formValues.city) {
			const locationParams = getLocationParams(formValues.city)

			queryParams = {
				...queryParams,
				...locationParams
			}
		}

		if (formValues.availableReservations.dateFrom) {
			if (formValues.availableReservations.dateTo) {
				queryParams = {
					...queryParams,
					avResTimeSlotDateFrom: formValues.availableReservations.dateFrom.format(DEFAULT_DATE_FORMAT),
					avResTimeSlotDateTo: formValues.availableReservations.dateTo.format(DEFAULT_DATE_FORMAT)
				}
			} else {
				queryParams = {
					...queryParams,
					avResTimeSlotDate: formValues.availableReservations.dateFrom.format(DEFAULT_DATE_FORMAT)
				}
			}

			if (formValues.availableReservations.timeSlot !== RESERVATIONS_TIME_SLOTS.ANY) {
				queryParams = {
					...queryParams,
					avResTimeSlotDayPart: formValues.availableReservations.timeSlot
				}
			}
		}

		// we need to set IS_SALONS_SEARCH attribute to session storage, so we can send a correct Google Analytics event when the new page is rendered
		// check getViewItemListEvent function for more info
		sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_SALONS_SEARCH, 'true')

		let href: string
		const foundCategory = categories.find((category) => category.id === formValues.industryID)
		const slugName = foundCategory?.seoSlug || foundCategory?.id

		if (slugName) {
			href = PAGE_LINKS['/salons/all/:categorySlug'](locale, slugName, queryParams)
		} else {
			href = PAGE_LINKS['/salons/all'](locale, queryParams)
		}

		window.location.href = href
	}

	return (
		<HomePageContainer>
			<SC.SalonsBookingForm onSubmit={handleSubmit(handleSubmitFilter)} id={FORM.BOOKING_FORM}>
				<SC.FormFields>
					<HookFormField
						control={control}
						name={'city'}
						component={SalonsAndCitiesSearchFormField}
						label={messages['Search for a city or a salon']}
						labelScreenReaderOnly
						selectStyle={'no-borders'}
						placeholder={messages['Search for a city or a salon']}
						suffixIcon={null}
						limits={{ limitSalons: 3, limitCategories: 0, limitCities: 3 }}
						showMyLocationOption={true}
						isLoadingValue={isLoadingSearchValue}
						onFocus={sendFormStartInteractionEvent}
						dropdownStyle={{ zIndex: BOOKING_FORM_Z_INDEXES.DROPDOWN }}
					/>
					<HookFormField
						control={control}
						name={'availableReservations'}
						component={DateTimeSlotPickerBookingFormField}
						label={messages.Date}
						labelScreenReaderOnly
						defaultValue={'now'}
						maxRange={maxAvResTimeSlotDateRange}
						onFocus={sendFormStartInteractionEvent}
					/>
					<HookFormField
						control={control}
						name={'industryID'}
						component={SelectFormField<IndustryValue, IndustryOption>}
						label={messages['Select industry']}
						labelScreenReaderOnly
						selectStyle={'no-borders'}
						prefixIcon={<CategoryIcon />}
						placeholder={`${messages['Select industry']}...`}
						allowClear
						optionRender={IndustryOptionRender}
						options={industryOptions}
						listHeight={450}
						onFocus={sendFormStartInteractionEvent}
						dropdownStyle={{ zIndex: BOOKING_FORM_Z_INDEXES.DROPDOWN }}
					/>
				</SC.FormFields>
				<SC.SearchButton type={'submit'} disabled={!watch('city')}>
					{messages.Search}
				</SC.SearchButton>
			</SC.SalonsBookingForm>
		</HomePageContainer>
	)
}

export default SalonsBookingForm
